import type { GroupId } from "@repo/app-types/entities/group";
import type { TeammatePreferences } from "@repo/app-types/entities/teammate";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { useUpdateTeammateMutation } from "../../api/entities/teammate.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { FilterContext, type FilterContextValue } from "./filterContext.js";
import { type FilterValues, defaultFilterValues } from "./filterState.js";
import { useCompanyDefaultDateRange } from "./useCompanyDefaultDateRange.js";

export interface FilterProviderProps {
  children?: ReactNode;
}

export const FilterProvider: FC<FilterProviderProps> = (props) => {
  // Maintain the state of the filters
  const { changedValues, onChange, onReset } = useChangedValues();

  // Load teammate's preferred default teammate group ids
  const { defaultTeammateGroupIds, saveDefaultTeammateGroupIds } =
    useDefaultTeammateGroupIds();

  // Save the teammates' preferred default teammate group ids when they
  // change. We write to the new multi-select preference field; the
  // legacy single-id preference is left untouched so older clients still
  // see a sensible default until they too migrate.
  useEffect(() => {
    const ids = changedValues.teammateGroupIds;
    if (ids !== undefined) {
      saveDefaultTeammateGroupIds(ids);
    }
  }, [changedValues.teammateGroupIds, saveDefaultTeammateGroupIds]);

  // Honor the company's `performance_default_date_range` setting when set.
  const companyDefaultDateRange = useCompanyDefaultDateRange();

  // Use the teammate preferences to compute the default filter values
  const effectiveDefaultValues: FilterValues = useMemo(
    () => ({
      ...defaultFilterValues,
      dateRange: companyDefaultDateRange ?? defaultFilterValues.dateRange,
      // Optimistically use the changed value, because the expectations is that it will be saved as the teammate's default
      teammateGroupIds:
        changedValues.teammateGroupIds !== undefined
          ? changedValues.teammateGroupIds
          : defaultTeammateGroupIds,
    }),
    [changedValues, companyDefaultDateRange, defaultTeammateGroupIds],
  );

  // Use the defaults and the user's changes to compute the true current filter values
  const effectiveValues: FilterValues = useMemo(
    () => ({
      ...effectiveDefaultValues,
      ...changedValues,
    }),
    [changedValues, effectiveDefaultValues],
  );

  const contextValue = useMemo<FilterContextValue>(
    () => ({
      defaultValues: effectiveDefaultValues,
      values: effectiveValues,
      changedValues,
      onChange,
      onReset,
    }),
    [effectiveDefaultValues, effectiveValues, changedValues, onChange, onReset],
  );

  return (
    <FilterContext.Provider value={contextValue}>
      {props.children}
    </FilterContext.Provider>
  );
};

/** Loads and fetches the user's preferred teammate group ids. */
function useDefaultTeammateGroupIds() {
  const teammate = useAppSelector(selectTeammate);
  const teammateId = teammate?.id;
  const defaultTeammateGroupIds = computeDefaultTeammateGroupIds(
    teammate?.settings.preferences,
  );

  const [updateTeammate] = useUpdateTeammateMutation();

  const saveDefaultTeammateGroupIds = useCallback(
    async (groupIds: readonly GroupId[] | null) => {
      if (!teammateId) {
        return;
      }

      await updateTeammate({
        id: teammateId,
        patchPayload: {
          preferences: {
            team_performance_filter_group_ids: groupIds
              ? [...groupIds]
              : groupIds,
          },
        },
      }).unwrap();
    },
    [teammateId, updateTeammate],
  );

  return {
    defaultTeammateGroupIds,
    saveDefaultTeammateGroupIds,
  };
}

/**
 * Resolve the teammate's preferred default teammate group ids from their
 * stored preferences. Prefers the multi-select
 * `team_performance_filter_group_ids` field when it has been written
 * (`null` or array). Falls back to the legacy single-id
 * `team_performance_filter_group_id` when the multi-select field has
 * never been set, so existing persisted preferences keep working until
 * the next save migrates them. Returns `undefined` while the teammate
 * (and therefore preferences) is still loading.
 */
function computeDefaultTeammateGroupIds(
  preferences: TeammatePreferences | undefined,
): readonly GroupId[] | null | undefined {
  if (preferences === undefined) {
    return undefined;
  }
  if (preferences.team_performance_filter_group_ids !== undefined) {
    return preferences.team_performance_filter_group_ids;
  }
  // Read-only fallback while the legacy field is being migrated; new
  // writes always go through `team_performance_filter_group_ids`.
  const legacySingleId = preferences.team_performance_filter_group_id;
  if (legacySingleId === null) {
    return null;
  }
  return [legacySingleId];
}

type ValuesChangedAction = {
  type: "changed";
  payload: Partial<FilterValues>;
};

type ValuesResetAction = {
  type: "reset";
};

type ValuesAction = ValuesChangedAction | ValuesResetAction;

const initialChangedValues: Partial<FilterValues> = Object.freeze({});

function reduceChangedValues(
  state: Partial<FilterValues>,
  action: ValuesAction,
): Partial<FilterValues> {
  switch (action.type) {
    case "changed":
      return {
        ...state,
        ...action.payload,
      };

    case "reset":
      return initialChangedValues;
  }
}

function useChangedValues() {
  const [changedValues, dispatch] = useReducer(
    reduceChangedValues,
    initialChangedValues,
  );

  const onChange = useCallback((newValues: Partial<FilterValues>) => {
    dispatch({ type: "changed", payload: newValues });
  }, []);

  const onReset = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  return { changedValues, onChange, onReset };
}
